if (!customElements.get('quick-add-modal')) {
  customElements.define(
    'quick-add-modal',
    class QuickAddModal extends ModalDialog {
      constructor() {
        super();
        this.modalContent = this.querySelector('[id^="QuickAddInfo-"]');
        this.mediaPreloadCache = new Set();
        this.productHTMLCache = new Map();
        this.pendingProductRequests = new Map();

        this.setupTriggerPrefetch();

        this.addEventListener('product-info:loaded', ({ target }) => {
          target.addPreProcessCallback(this.preprocessHTML.bind(this));
          this.primeMediaCache(target);
        });
      }

      hide(preventFocus = false) {
        const cartNotification = document.querySelector('cart-notification') || document.querySelector('cart-drawer');
        if (cartNotification) cartNotification.setActiveElement(this.openedBy);
        this.modalContent.innerHTML = '';

        if (preventFocus) this.openedBy = null;
        super.hide();
      }

      show(opener) {
        opener.setAttribute('aria-disabled', true);
        opener.classList.add('loading');
        opener.querySelector('.loading__spinner').classList.remove('hidden');

        this.fetchProductHTML(opener.getAttribute('data-product-url'))
          .then((productHTML) => {
            HTMLUpdateUtility.setInnerHTML(this.modalContent, productHTML);

            const loadedProduct = this.modalContent.querySelector('product-info');
            if (loadedProduct) this.primeMediaCache(loadedProduct);

            if (window.Shopify && Shopify.PaymentButton) {
              Shopify.PaymentButton.init();
            }
            if (window.ProductModel) window.ProductModel.loadShopifyXR();

            super.show(opener);
          })
          .finally(() => {
            opener.removeAttribute('aria-disabled');
            opener.classList.remove('loading');
            opener.querySelector('.loading__spinner').classList.add('hidden');
          });
      }

      setupTriggerPrefetch() {
        const openerButton = document.querySelector(`[data-modal="#${this.id}"] [data-product-url]`);
        if (!openerButton) return;

        const prefetch = () => {
          this.fetchProductHTML(openerButton.getAttribute('data-product-url')).catch(() => {});
        };

        openerButton.addEventListener('mouseenter', prefetch, { once: true });
        openerButton.addEventListener('focus', prefetch, { once: true });
        openerButton.addEventListener('touchstart', prefetch, { once: true, passive: true });
      }

      fetchProductHTML(productUrl) {
        if (!productUrl) return Promise.reject(new Error('Missing product URL for quick add modal'));

        if (this.productHTMLCache.has(productUrl)) {
          return Promise.resolve(this.productHTMLCache.get(productUrl));
        }

        if (this.pendingProductRequests.has(productUrl)) {
          return this.pendingProductRequests.get(productUrl);
        }

        const request = fetch(productUrl)
          .then((response) => response.text())
          .then((responseText) => {
            const responseHTML = new DOMParser().parseFromString(responseText, 'text/html');
            const productElement = responseHTML.querySelector('product-info');
            if (!productElement) throw new Error('Quick add product content not found');

            this.preprocessHTML(productElement);
            const productHTML = productElement.outerHTML;
            this.productHTMLCache.set(productUrl, productHTML);
            return productHTML;
          })
          .finally(() => {
            this.pendingProductRequests.delete(productUrl);
          });

        this.pendingProductRequests.set(productUrl, request);
        return request;
      }

      preprocessHTML(productElement) {
        productElement.classList.forEach((classApplied) => {
          if (classApplied.startsWith('color-') || classApplied === 'gradient')
            this.modalContent.classList.add(classApplied);
        });
        this.preventDuplicatedIDs(productElement);
        this.removeDOMElements(productElement);
        this.removeGalleryListSemantic(productElement);
        this.updateImageSizes(productElement);
        this.preventVariantURLSwitching(productElement);
      }

      preventVariantURLSwitching(productElement) {
        productElement.setAttribute('data-update-url', 'false');
      }

      removeDOMElements(productElement) {
        const pickupAvailability = productElement.querySelector('pickup-availability');
        if (pickupAvailability) pickupAvailability.remove();

        const productModal = productElement.querySelector('product-modal');
        if (productModal) productModal.remove();

        const modalDialog = productElement.querySelectorAll('modal-dialog');
        if (modalDialog) modalDialog.forEach((modal) => modal.remove());
      }

      preventDuplicatedIDs(productElement) {
        const sectionId = productElement.dataset.section;

        const oldId = sectionId;
        const newId = `quickadd-${sectionId}`;
        productElement.innerHTML = productElement.innerHTML.replaceAll(oldId, newId);
        Array.from(productElement.attributes).forEach((attribute) => {
          if (attribute.value.includes(oldId)) {
            productElement.setAttribute(attribute.name, attribute.value.replace(oldId, newId));
          }
        });

        productElement.dataset.originalSection = sectionId;
      }

      removeGalleryListSemantic(productElement) {
        const galleryList = productElement.querySelector('[id^="Slider-Gallery"]');
        if (!galleryList) return;

        galleryList.setAttribute('role', 'presentation');
        galleryList.querySelectorAll('[id^="Slide-"]').forEach((li) => li.setAttribute('role', 'presentation'));
      }

      updateImageSizes(productElement) {
        const product = productElement.querySelector('.product');
        const desktopColumns = product?.classList.contains('product--columns');
        if (!desktopColumns) return;

        const mediaImages = product.querySelectorAll('.product__media img');
        if (!mediaImages.length) return;

        let mediaImageSizes =
          '(min-width: 1000px) 715px, (min-width: 750px) calc((100vw - 11.5rem) / 2), calc(100vw - 4rem)';

        if (product.classList.contains('product--medium')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '605px');
        } else if (product.classList.contains('product--small')) {
          mediaImageSizes = mediaImageSizes.replace('715px', '495px');
        }

        mediaImages.forEach((img) => img.setAttribute('sizes', mediaImageSizes));
      }

      primeMediaCache(productElement) {
        const mediaImages = productElement.querySelectorAll('.product__media img, .deferred-media__poster img');
        if (!mediaImages.length) return;

        mediaImages.forEach((img, index) => {
          img.setAttribute('loading', 'eager');
          img.setAttribute('decoding', 'async');
          if (index === 0) img.setAttribute('fetchpriority', 'high');

          const cacheKey = [img.currentSrc || img.src, img.getAttribute('srcset') || '', img.getAttribute('sizes') || '']
            .join('|')
            .trim();

          if (!cacheKey || this.mediaPreloadCache.has(cacheKey)) return;
          this.mediaPreloadCache.add(cacheKey);

          const preloader = new Image();
          preloader.decoding = 'async';

          const sizes = img.getAttribute('sizes');
          const srcset = img.getAttribute('srcset');
          if (sizes) preloader.sizes = sizes;
          if (srcset) preloader.srcset = srcset;
          preloader.src = img.currentSrc || img.src;
        });
      }
    }
  );
}
